import { useQuery } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Card, CardContent } from '@/Domain/components/Shared/Card/Card';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { QuickchatAiIcon } from '@/Domain/components/icons/QuickchatAiWordmark.icon';

const CONTINUE_PATH = '/hubspot_partner_install/continue/';

export function HubspotConnectAgentPage() {
  const { t } = useTranslation('hubspot_connect');
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') ?? '';
  // Eligible (editor+) scenario ids computed server-side by the continue endpoint; empty means show all.
  const eligibleIds = (searchParams.get('scenarios') ?? '').split(',').filter(Boolean);

  const assistantsQuery = useQuery<AIAssistantsData>({ queryKey: [QueryKey.aiAssistants.get] });

  // No returnUrl means we're not inside a HubSpot install flow; there's nothing to select for.
  useEffect(() => {
    if (!returnUrl) {
      window.location.assign('/');
    }
  }, [returnUrl]);

  if (!returnUrl) {
    return null;
  }

  const agents = (assistantsQuery.data?.all_ai_assistants ?? []).filter(
    (agent) => eligibleIds.length === 0 || eligibleIds.includes(agent.scenario_id),
  );

  const handleSelect = (scenarioId: string): void => {
    // Full document navigation: the continue endpoint mints the OAuth state and redirects back to HubSpot.
    const query = new URLSearchParams({ returnUrl, scenario_id: scenarioId });
    window.location.assign(`${CONTINUE_PATH}?${query.toString()}`);
  };

  const handleCreateAgent = (): void => {
    // add_new_ai replays a validated `next`, so the fresh agent resumes the install via continue.
    const next = `${CONTINUE_PATH}?${new URLSearchParams({ returnUrl }).toString()}`;
    window.location.assign(`/add_new_ai/?${new URLSearchParams({ next }).toString()}`);
  };

  if (assistantsQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (assistantsQuery.isError && !assistantsQuery.data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-foreground">
        <p className="text-muted-foreground">{t('error')}</p>
        <Button
          variant="dark"
          onClick={() => assistantsQuery.refetch()}
          data-testid="hubspot-connect-retry"
        >
          {t('retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-background p-6 text-foreground">
      <QuickchatAiIcon
        className="absolute top-6 left-6 scale-[1.2] text-foreground"
        data-testid="hubspot-connect-page-logo"
      />

      <div className="w-full max-w-xl space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>

        <div className="space-y-3" data-testid="hubspot-connect-agent-list">
          {agents.length === 0 && (
            <div
              className="flex flex-col items-center gap-4 rounded-xl border border-border p-8 text-center"
              data-testid="hubspot-connect-empty"
            >
              <p className="text-muted-foreground">{t('empty')}</p>
              <Button
                variant="dark"
                onClick={handleCreateAgent}
                data-testid="hubspot-connect-create"
              >
                {t('create_agent')}
              </Button>
            </div>
          )}
          {agents.map((agent) => (
            <Card key={agent.scenario_id} data-testid={`hubspot-connect-card-${agent.scenario_id}`}>
              <CardContent className="flex items-center justify-between gap-4 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  {agent.avatar_image ? (
                    <img
                      src={agent.avatar_image}
                      alt=""
                      className="size-8 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <Bot className="size-8 shrink-0 rounded-full bg-muted p-1.5 text-muted-foreground" />
                  )}
                  <span className="truncate font-medium">
                    {agent.name || agent.fallback_name || t('untitled_agent')}
                  </span>
                </div>
                <Button
                  variant="dark"
                  onClick={() => handleSelect(agent.scenario_id)}
                  data-testid={`hubspot-connect-select-${agent.scenario_id}`}
                >
                  {t('connect')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
